import { Fragment, useState, useMemo, useCallback, cloneElement } from "react";
import { useHistory } from "react-router-dom";
import {
  AvatarGroup,
  Avatar,
  Popover,
  PopoverTrigger,
  PopoverSurface,
  Tooltip,
} from "@fluentui/react-components";
import PropTypes from "prop-types";
import { FluentButton } from "../../../../components/FluentUI/Fluent-Button";
import { FluentBadge } from "../../../../components/FluentUI/Fluent-Badge";
import FluentUiIcon from "../../../../assets/fluent-ui-icon";
import { filterBy, orderBy } from "@progress/kendo-data-query";
import EmptyStateNoDealsOnFilter from "../../../../assets/img/Errors/NoDealsOnFilter.svg";
import EmptyStateImg from "../../../../assets/img/Errors/empty.svg";
import EmptyStateClipboard from "../../../../assets/img/Errors/emptyDataClipboard.svg";

// ---------------------------------------------------------------------------
// localStorage helpers
// ---------------------------------------------------------------------------

const getStorageKey = (key, userId) => `${key}_user_${userId}`;

const normalizeLastUpdatedFilter = (filter) => {
  if (!filter || !Array.isArray(filter.filters)) return filter;
  return {
    ...filter,
    filters: filter.filters.map((f) => {
      if (Array.isArray(f.filters)) return normalizeLastUpdatedFilter(f);
      if (f.field === "LastUpdated" || f.field === "LastUpdatedString") {
        const d = f.value ? new Date(f.value) : null;
        return { ...f, value: d instanceof Date && !isNaN(d) ? d : null };
      }
      return f;
    }),
  };
};

export const getInitialDetailState = (userId, baseKey = "techStaffer") => {
  if (!userId) return { sort: [], filter: null };
  try {
    const sortRaw = localStorage.getItem(getStorageKey(`${baseKey}Sort`, userId));
    const filterRaw = localStorage.getItem(getStorageKey(`${baseKey}Filter`, userId));
    const sort = sortRaw ? JSON.parse(sortRaw) : [];
    const filter = filterRaw ? normalizeLastUpdatedFilter(JSON.parse(filterRaw)) : null;
    return { sort, filter };
  } catch (e) {
    console.warn("Failed to parse localStorage", e);
    return { sort: [], filter: null };
  }
};

export const saveDetailStateToStorage = (userId, newState, searchValue, baseKey = "techStaffer") => {
  if (!userId || searchValue?.trim()) return;
  localStorage.setItem(getStorageKey(`${baseKey}Sort`, userId), JSON.stringify(newState.sort));
  localStorage.setItem(getStorageKey(`${baseKey}Filter`, userId), JSON.stringify(newState.filter));
};

export const clearDetailStateFromStorage = (userId, baseKey = "techStaffer") => {
  if (!userId) return;
  localStorage.removeItem(getStorageKey(`${baseKey}Sort`, userId));
  localStorage.removeItem(getStorageKey(`${baseKey}Filter`, userId));
};

// ---------------------------------------------------------------------------
// EditIconButton
// ---------------------------------------------------------------------------

export const EditIconButton = ({ projectId, projectName, disabled = false, tooltipContent }) => {
  const [hovered, setHovered] = useState(false);
  const [tooltipVisible, setTooltipVisible] = useState(false);
  const history = useHistory();

  const defaultTooltip = (
    <span>Edit <span style={{ fontWeight: 500 }}>{projectName}</span> Deal</span>
  );

  return (
    <div className={`edit-button-wrapper ${tooltipVisible ? "tooltip-open" : ""}`}>
      <Tooltip
        content={tooltipContent || defaultTooltip}
        relationship="label"
        positioning="above"
        onVisibleChange={(_, data) => setTooltipVisible(data.visible)}
      >
        <div
          onMouseEnter={() => setHovered(true)}
          onMouseLeave={() => setHovered(false)}
          style={{ display: "inline-block" }}
        >
          <FluentButton
            icon={
              <FluentUiIcon
                name={hovered && !disabled ? "EditFilled" : "EditRegular"}
                color={disabled ? "#D0D0D0" : "#0F6CBD"}
                size={20}
              />
            }
            onClick={() => {
              if (!disabled) history.push({ pathname: `/projects/edit/${projectId}`, state: { fromDashboard: true } });
            }}
            appearance="transparent"
            className="edit-hover-button"
            disabled={disabled}
          />
        </div>
      </Tooltip>
    </div>
  );
};

EditIconButton.propTypes = {
  projectId: PropTypes.number.isRequired,
  projectName: PropTypes.string,
  disabled: PropTypes.bool,
  tooltipContent: PropTypes.node,
};

// ---------------------------------------------------------------------------
// Empty states
// ---------------------------------------------------------------------------

export const TechStafferNoDealOnFilterState = ({ resetDataState }) => (
  <div className="empty-state-container mt-4">
    <img src={EmptyStateNoDealsOnFilter} alt="No Data" />
    <p className="empty-state-header">
      No results found with the current filters!{" "}
      <FluentUiIcon name="SearchRegular" color="#424242" size={20} />
    </p>
    <p className="empty-state-subtext">
      Try{" "}
      <button
        type="button"
        onClick={resetDataState}
        className="create-deal-link"
      >
        adjusting the filter options
      </button>{" "}
      to refine your results.
    </p>
  </div>
);

export const TechStafferNoBankersOnSearchState = ({ searchValue, resetDataState }) => (
  <div className="empty-state-container mt-4">
    <img src={EmptyStateNoDealsOnFilter} alt="No Data" />
    <p className="empty-state-header">No Bankers match your search</p>
    <p className="empty-state-subtext">
      No GIB/Technology bankers match &ldquo;<strong>{searchValue}</strong>&rdquo;. Check the spelling, or{" "}
      <button
        type="button"
        onClick={resetDataState}
        className="create-deal-link"
      >
        clear the search
      </button>{" "}
      to see everyone.
    </p>
  </div>
);

TechStafferNoBankersOnSearchState.propTypes = {
  searchValue: PropTypes.string,
  resetDataState: PropTypes.func,
};

export const TechStafferNoDealOnSearchState = ({ searchValue, resetDataState }) => (
  <div className="empty-state-container mt-4">
    <img src={EmptyStateNoDealsOnFilter} alt="No Data" />
    <p className="empty-state-header">
      Hmm, nothing matched — but there are more places to look!{" "}
      <FluentUiIcon name="SearchRegular" color="#424242" size={20} />
    </p>
    <p className="empty-state-subtext">
      We couldn&apos;t find any Deals you&apos;re a Member of based on your search for &ldquo;<strong>{searchValue}</strong>&rdquo;.{" "}
      <button
        type="button"
        onClick={resetDataState}
        className="create-deal-link"
      >
        Clear the search
      </button>{" "}
      to see all Deals.
    </p>
  </div>
);

TechStafferNoDealOnSearchState.propTypes = {
  searchValue: PropTypes.string,
  resetDataState: PropTypes.func,
};

export const TechStafferNoBankersEmptyState = () => (
  <div className="empty-state-container--widget" data-testid="no-bankers-empty-state">
    <img src={EmptyStateClipboard} alt="No Data" />
    <p className="empty-state-header">No Bankers onboarded yet.</p>
    <p className="empty-state-subtext">
      There are currently no GIB/Technology Bankers onboarded to TRIYO. Once they are onboarded by RBC&apos;s AACG team, they will appear here.
    </p>
    <p className="empty-state-subtext">
      Need help in the meantime? TRIYO&apos;s Support Team is happy to assist.
    </p>
    <FluentButton
      appearance="secondaryWithOutline"
      text="Contact TRIYO Support"
      onClick={() => window.open("mailto:support@triyo.io", "_self")}
    />
  </div>
);

export const TechStafferNoDealsEmptyState = ({ isStaffer }) => {
  const history = useHistory();
  return (
    <div className="empty-state-container-staffer mt-4" data-testid="no-deals-empty-state">
      <img src={EmptyStateImg} alt="No Data" />
      <p className="empty-state-header">No Active Deals on TRIYO yet.</p>
      <p className="empty-state-subtext">
        Any Deals created will show up here.{" "}
        {isStaffer ? (
          <>
            Start a{" "}
            <button
              type="button"
              onClick={() => history.push({ pathname: "/projects/create", state: { fromDashboard: true } })}
              className="create-deal-link"
            >
              new one
            </button>
          </>
        ) : null}
      </p>
    </div>
  );
};

const ROLE_GROUP_ORDER = ["Analyst", "Associate"];

const MEMBER_GROUP_BADGE_COLORS = {
  Analyst: "brand",
  Associate: "brand",
};

export const extractGroupName = (role) => {
  if (!role) return "Other";
  if (role.toLowerCase().includes("analyst")) return "Analyst";
  if (role.toLowerCase().includes("associate")) return "Associate";
  return "Other";
};

const sortByLastUpdatedDesc = (arr) =>
  arr.sort((a, b) => {
    if (!a.LastUpdated && !b.LastUpdated) return 0;
    if (!a.LastUpdated) return 1;
    if (!b.LastUpdated) return -1;
    return b.LastUpdated - a.LastUpdated;
  });

export const flattenTechMembers = (techMembers) => {
  const mapped = (techMembers || []).map((item) => {
    const member = item.TechMember ?? item;
    const nestedProjects = item.TechProjects ?? [];
    return {
      Banker: member.User?.FullName ?? "–",
      UserId: member.User?.UserId,
      Role: member.User?.Role,
      GroupName: extractGroupName(member.User?.Role),
      LastWeekHours: member.LastWeekHours,
      WeekendHours: member.WeekendHours,
      TotalHours: member.TotalHours,
      CurrentWeekHours: member.CurrentWeekHours,
      LastUpdated: member.LastUpdated ? new Date(member.LastUpdated) : null,
      HasOldData: member.HasOldData,
      TechProjects: nestedProjects,
    };
  });
  return sortByLastUpdatedDesc(mapped);
};

export const flattenTechProjects = (techProjects) => {
  const mapped = (techProjects || []).map((item) => {
    const project = item.TechProject ?? item;
    const nestedMembers = item.TechMembers ?? [];
    const memberNames = (project.Members || [])
      .map((m) => m.User?.Name)
      .filter(Boolean);
    const seniorMemberNames = (project.SeniorMembers || [])
      .map((m) => (typeof m === "string" ? m : m.User?.Name || m.Name))
      .filter(Boolean);
    return {
      ProjectId: project.ProjectId,
      ProjectName: project.ProjectName ?? "–",
      LastWeekHours: project.LastWeekHours,
      WeekendHours: project.WeekendHours,
      TotalHours: project.TotalHours,
      CurrentWeekHours: project.CurrentWeekHours,
      LastUpdated: project.LastUpdated ? new Date(project.LastUpdated) : null,
      IsOverDue: project.IsOverDue,
      IsNew: project.IsNew,
      HasOldData: project.HasOldData,
      Members: project.Members || [],
      MemberNamesArray: memberNames,
      SeniorMembers: project.SeniorMembers || [],
      SeniorMemberNamesArray: seniorMemberNames,
      TechMembers: nestedMembers,
    };
  });
  return sortByLastUpdatedDesc(mapped);
};

const toLocalMidnight = (v) => {
  if (!v) return null;
  const d = v instanceof Date ? v : new Date(v);
  return isNaN(d.getTime()) ? null : new Date(d.getFullYear(), d.getMonth(), d.getDate());
};

export const getMemberFilteredSortedData = (flatData, filter, sort) => {
  let temp = (flatData || []).map((r) => ({
    ...r,
    LastUpdated: toLocalMidnight(r.LastUpdated),
  }));

  if (filter?.filters?.length) {
    temp = filterBy(temp, filter);
  }

  const sorted = sort?.length ? orderBy(temp, sort) : temp;

  return [...sorted].sort((a, b) => {
    if (a.GroupName === b.GroupName) return 0;
    const aIdx = ROLE_GROUP_ORDER.indexOf(a.GroupName);
    const bIdx = ROLE_GROUP_ORDER.indexOf(b.GroupName);
    if (aIdx === -1 && bIdx === -1) return 0;
    if (aIdx === -1) return 1;
    if (bIdx === -1) return -1;
    return aIdx - bIdx;
  });
};

export const getDealFilteredSortedData = (flatData, filter, sort) => {
  let temp = (flatData || []).map((r) => ({
    ...r,
    LastUpdated: toLocalMidnight(r.LastUpdated),
  }));

  const memberFilters = [];
  const seniorMemberFilters = [];
  (filter?.filters || []).forEach((f) => {
    if (f.field === "Members") memberFilters.push((f.value ?? "").toLowerCase());
    else if (f.field === "SeniorMembers") seniorMemberFilters.push((f.value ?? "").toLowerCase());
    else if (Array.isArray(f.filters)) {
      f.filters.forEach((inner) => {
        if (inner.field === "Members") memberFilters.push((inner.value ?? "").toLowerCase());
        else if (inner.field === "SeniorMembers") seniorMemberFilters.push((inner.value ?? "").toLowerCase());
      });
    }
  });

  if (memberFilters.length) {
    temp = temp.filter((item) =>
      item.MemberNamesArray?.some((name) =>
        memberFilters.includes((name || "").toLowerCase())
      )
    );
  }

  if (seniorMemberFilters.length) {
    temp = temp.filter((item) =>
      item.SeniorMemberNamesArray?.some((name) =>
        seniorMemberFilters.includes((name || "").toLowerCase())
      )
    );
  }

  const kendoCompatibleFilters =
    filter?.filters?.filter(
      (f) =>
        f.field !== "Members" &&
        f.field !== "SeniorMembers" &&
        !f.filters?.some((inner) => inner.field === "Members" || inner.field === "SeniorMembers")
    ) ?? [];

  const processedFilter = { ...filter, filters: kendoCompatibleFilters };
  if (processedFilter.filters?.length) {
    temp = filterBy(temp, processedFilter);
  }

  return sort?.length ? orderBy(temp, sort) : temp;
};

export const useTechMemberGroupToggle = (
  initialState = { Analyst: true, Associate: true }
) => {
  const [groupState, setGroupState] = useState(initialState);

  const toggleGroup = useCallback((groupName) => {
    setGroupState((prev) => ({ ...prev, [groupName]: !prev[groupName] }));
  }, []);

  return { groupState, toggleGroup, setGroupState };
};

export const getAccordionTechMemberRowRender = (
  filteredData,
  groupState,
  toggleGroup,
  colspan,
  onRowClick = null
) => {
  return (trElement, props) => {
    if (props.dataItem?.isEmptyRow) return trElement;

    const groupName = props.dataItem.GroupName;
    const isFirstInGroup =
      props.dataIndex ===
      filteredData.findIndex((item) => item.GroupName === groupName);
    const isVisible = groupState[groupName] ?? true;

    if (!isFirstInGroup && !isVisible) return null;

    const clickableRow = onRowClick
      ? cloneElement(trElement, {
          onClick: () => onRowClick(props.dataItem),
          style: { ...(trElement.props.style || {}), cursor: "pointer" },
        })
      : trElement;

    if (!isFirstInGroup) return isVisible ? clickableRow : null;

    const activeCount = filteredData.filter(
      (item) => item.GroupName === groupName
    ).length;

    return (
      <Fragment key={`group-${groupName}-${props.dataIndex}`}>
        <tr
          className="custom-group-header"
          style={{
            backgroundColor: "#fff",
            height: "48px",
            borderBottom: "1px solid #edebe9",
            cursor: "pointer",
            lineHeight: "140%",
          }}
          onClick={() => toggleGroup(groupName)}
        >
          <td
            colSpan={colspan}
            style={{
              padding: "0 16px",
              verticalAlign: "middle",
              borderBottom: "1px solid #edebe9",
              borderTop: "1px solid #edebe9",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
              <FluentButton
                className="accordion-toggle-button"
                disabled={false}
                icon={
                  <FluentUiIcon
                    name={isVisible ? "ChevronUpRegular" : "ChevronDownRegular"}
                    size={14}
                    disabled={false}
                  />
                }
                appearance="transparent"
                onClick={(e) => {
                  e.stopPropagation();
                  toggleGroup(groupName);
                }}
              />
              <span
                style={{
                  fontWeight: "600",
                  fontSize: "14px",
                  marginLeft: "-10px",
                }}
              >
                {groupName ? groupName + "s" : ""}
              </span>
              <FluentBadge
                appearance="tint"
                size="large"
                color={MEMBER_GROUP_BADGE_COLORS[groupName] || "brand"}
                text={`${activeCount} Banker${activeCount !== 1 ? "s" : ""}`}
              />
            </div>
          </td>
        </tr>
        {isVisible && clickableRow}
      </Fragment>
    );
  };
};
export const SeniorMembersCell = ({ names }) => {
  const [open, setOpen] = useState(false);
  const uniqueNames = useMemo(() => [...new Set(names || [])], [names]);

  if (!uniqueNames.length) return <span>–</span>;

  const shown = uniqueNames.slice(0, 3);
  const extra = uniqueNames.length - 3;

  return (
    <div onMouseEnter={() => setOpen(true)} onMouseLeave={() => setOpen(false)}>
      <Popover
        open={open}
        onOpenChange={(_, d) => setOpen(d.open)}
        positioning={{ position: "below-start" }}
      >
        <PopoverTrigger disableButtonEnhancement>
          <div className="deal-team-avatar__trigger">
            <AvatarGroup layout="stack">
              {shown.map((name, i) => (
                <div key={i} className="deal-team-avatar__stacked-item" style={{ zIndex: 10 - i }}>
                  <Avatar name={name} size={24} color="seafoam" />
                </div>
              ))}
              {extra > 0 && (
                <div className="deal-team-avatar__stacked-item--extra">
                  <Avatar initials={`+${extra}`} color="neutral" size={24} />
                </div>
              )}
            </AvatarGroup>
          </div>
        </PopoverTrigger>
        <PopoverSurface>
          <div className="deal-team-avatar__popover-list">
            {uniqueNames.map((name, i) => (
              <div key={i} className="deal-team-avatar__popover-row">
                <Avatar name={name} size={24} color="seafoam" />
                <span className="deal-team-avatar__member-name">{name}</span>
              </div>
            ))}
          </div>
        </PopoverSurface>
      </Popover>
    </div>
  );
};

SeniorMembersCell.propTypes = {
  names: PropTypes.arrayOf(PropTypes.string),
};